/**
 * ROOM ALARMS — in-room surfacing (RELEASE-ALARMS.md §Rooms). The live integration-test alarms for
 * THIS room group. Exposes the tripped list + count so the room can show a right-aligned dynamic
 * ``Alarm (N)`` tab — shown ONLY when something is broken (count > 0), hidden when everything's clean.
 * Quiet by design: fetch on mount + tab-focus + a slow interval; `bump()` lets the room refetch right
 * after a run terminal (when a fresh verdict may have tripped). Fail-open to empty (a broken alarms
 * endpoint must never degrade the room).
 */
import { useCallback, useEffect, useState } from 'react';
import { fetchRoomAlarms, resetRoomAlarm } from '../services/dataPlaneApi';

const REFRESH_MS = 60 * 1000;

export function useRoomAlarms({ networkId, poolId, enabled = true } = {}) {
  const [alarms, setAlarms] = useState([]);
  const [trippedCount, setTrippedCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!enabled || !networkId || !poolId) return;
    try {
      const data = await fetchRoomAlarms(networkId, poolId);
      const rows = Array.isArray(data?.alarms) ? data.alarms : [];
      const tripped = rows.filter((a) => String(a.Status || '').toLowerCase() === 'tripped');
      setAlarms(tripped);
      setTrippedCount(Number.isFinite(data?.trippedCount) ? data.trippedCount : tripped.length);
    } catch {
      /* fail-open — never let the alarms endpoint degrade the room */
    }
  }, [enabled, networkId, poolId]);

  const reset = useCallback(async (name) => {
    if (!networkId || !poolId || !name) return;
    try { await resetRoomAlarm(networkId, poolId, name, { reset: true }); } catch { /* best-effort */ }
    refresh();
  }, [networkId, poolId, refresh]);

  useEffect(() => {
    if (!enabled || !networkId || !poolId) return undefined;
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [enabled, networkId, poolId, refresh]);

  return { alarms, trippedCount, refresh, bump: refresh, reset };
}

export default useRoomAlarms;
